import { Close } from "@mui/icons-material";
import { Button, FormControl, IconButton, InputLabel, MenuItem, Modal, Select } from "@mui/material";
import * as Sentry from "@sentry/react";
import { Row, Tooltip } from "antd";
import { levelsData } from "components/StoryWithAi/constants";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import QnALoader from "components/VocabQnAModal/QnALoader";
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { question_type_generate_more } from "utils/config/reference";
import { handleStreamResponse } from "utils/methods/QuestionCreateStreamHandle";
import { useUserProfileConfig } from "store/users/selectors";
import SentryWrap from "components/Sentry";
interface Props {
    assignmentAiModal: boolean;
    setassignmentAiModal: (value: boolean) => void;
    page: string;
    chapter: string;
    pageSrc: string;
    content_type: string;
}

interface Question {
    id?: number;
    question: string;
    answer: string;
}

interface APIResponse {
    success: boolean;
    data?: {
        title?: string;
        questions?: Question[];
    };
}

const languageMenuItems = [
    {
        key: 'spanish',
        label: 'Spanish'
    },
    {
        key: 'english',
        label: 'English'
    }
];
const ResourseAssignmentAiModal = forwardRef(({ 
    setassignmentAiModal, 
    page, 
    chapter, 
    pageSrc,  
    content_type ,
    assignmentAiModal 
}: Props, ref) => {
    const showAlert = useAlert();
    const [isQuestionModalOpen, setIsQuestionModalOpen] = useState<boolean>(false);
    const [ai_questions, setAiQuestions] = useState<Question[]>([]);
    const [loader, setLoader] = useState<boolean>(false);
    const [loaderMore, setLoaderMore] = useState<boolean>(false);
    const [title, setTitle] = useState<string>('');
    const [selectedTypes, setSelectedTypes] = useState<string[]>(
        question_type_generate_more.map(option => option.value)
    );
    const [generatedCount,SetGenerateCount] = useState(0);
    const questionRequestIdRef = useRef(0);
    const [proficiencyLevel ,setProficiencyLevel] = useState('');
    const [language,setLanguage] = useState('');
    const { isAiPermissionEnabled } = useUserProfileConfig();

    useEffect(() => {
        if (!isQuestionModalOpen) {
            questionRequestIdRef.current += 1;
            setAiQuestions([]);
            setLoader(false);
            setLoaderMore(false);
            SetGenerateCount(0);
        }
    }, [isQuestionModalOpen]);

    const handleGenerate = async (is_more?: boolean,languageKey?:string): Promise<void> => {
        if (!isAiPermissionEnabled) {
            showAlert('Please enable AI permission to generate questions.', 'error');
            return;
        }
        setLoader(!is_more);
        setLoaderMore(true);
        setassignmentAiModal(false);
        setIsQuestionModalOpen(true);
        const requestId = questionRequestIdRef.current + 1;
        questionRequestIdRef.current = requestId;
        
        try {
            const data = JSON.stringify({
                bookId: chapter,
                from_page: '',
                to_page: '',
                question_type: selectedTypes || [],
                resource: content_type === 'resources',
                proficiencyLevel: proficiencyLevel,
                question_language: (languageKey ? languageKey : language)?.toLowerCase()
            })
            await handleStreamResponse({
                path: "ai/generate/generate-questions-stream",
                body: data,
                onTitle: (title:any) => {
                    if (questionRequestIdRef.current !== requestId) return;
                    setTitle(title)
                    SetGenerateCount(0)
                },
                onQuestion: (question:any) => {
                    if (questionRequestIdRef.current !== requestId) return;
                    setAiQuestions(prev => [...prev, { ...question, id: Math.random() * 1000000000 }]);
                    SetGenerateCount(prev => prev + 1)
                    setLoader(false);
                    setLoaderMore(false);
                },
                onDone: () => {
                    if (questionRequestIdRef.current !== requestId) return;
                    setLoaderMore(false);
                    setLoader(false);
                    SetGenerateCount(0)
                    setLanguage('');
                },
                onError: () => {
                    if (questionRequestIdRef.current !== requestId) return;
                    setLoaderMore(false);
                    setLoader(false);
                    SetGenerateCount(0)
                    setLanguage('');
                }
            })
        } catch (error: any) {
            Sentry.captureException(`326 Error in handleGenerate: ${error}`);
            showAlert(error?.message || 'Something went wrong, please try with another page!','error');
            setassignmentAiModal(true);
            setIsQuestionModalOpen(false);
        } finally {
            setLoader(false);
            setLoaderMore(false);
        }
    };

    // 🔹 Exposing handleGenerate through ref
    // useImperativeHandle(ref, () => ({
    //     generateQuestions: ()=>{
    //         handleGenerate();
    //         setIsQuestionModalOpen(true);
    //     }
    // }));

    // const generated_question = useMemo(() => 
    //     ai_questions.map(k => ({ ...k, id: Math.random() * 1000000000 })), 
    //     [ai_questions]
    // );
    const disable = useMemo(() => !Boolean(proficiencyLevel) || !Boolean(language), [proficiencyLevel,language])

    return (
      <SentryWrap>
        <QnALoader
          assignmentAiModal={isQuestionModalOpen}
          setAssignmentAiModal={setIsQuestionModalOpen}
          handleGenerateMoreQnA={(topic, language) => handleGenerate(true, language)}
          generateQnALoader={loader}
          questionGenerator={ai_questions}
          page={page}
          assignmentTitle={title}
          chapter={chapter}
          pageSrc={pageSrc}
          loading={loaderMore}
          from={'ai_image'}
          selectedTypes={selectedTypes as never[]}
          setSelectedTypes={setSelectedTypes}
          qnaWord={undefined}
          pageNumber={undefined}
          generatedCount={generatedCount}
          setQuestionGenerator={setAiQuestions as any}
          resourceKeyAssignmentAi={`resource`}
          uniqueKeyAssignmentAi={''}
        />

        <Modal open={assignmentAiModal} style={{ height: '100%', width: '100%' }}>
          <ModalContent>
            <Row style={{ justifyContent: 'space-between' }}>
              <TitleText color={'black'}>Assignment With AI</TitleText>
              <IconButton
                onClick={() => {
                  questionRequestIdRef.current += 1;
                  setassignmentAiModal(false);
                  setIsQuestionModalOpen(false);
                  setAiQuestions([]);
                  setLoader(false);
                  setLoaderMore(false);
                  setProficiencyLevel('');
                  setLanguage('');
                }}
                style={{ padding: 0 }}
              >
                <Close color="secondary" />
              </IconButton>
            </Row>

            <Row
              gutter={[24, 24]}
              style={{ marginTop: 20, marginRight: 0, marginLeft: 0 }}
              className="floatingLabelBG"
            >
              <FormControl variant="outlined" style={{ minWidth: 200 }}>
                <InputLabel id="fromPageLabel">Proficiency Level</InputLabel>
                <Select
                  labelId="fromPageLabel"
                  id="fromPage"
                  value={proficiencyLevel}
                  onChange={(e) => setProficiencyLevel(e.target.value)}
                  label="From Page"
                >
                  {levelsData.map((item, index) => (
                    <MenuItem key={index} value={item?.label}>
                      <Tooltip zIndex={9999999} color={'#3d5f8f'} title={item?.label}>
                        {item?.label?.length > 40
                          ? `${item?.label?.substring(0, 40)}...`
                          : item?.label}
                      </Tooltip>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Row>

            <Row
              gutter={[24, 24]}
              style={{ marginTop: 20, marginRight: 0, marginLeft: 0 }}
              className="floatingLabelBG"
            >
              <FormControl variant="outlined" style={{ minWidth: 200 }}>
                <InputLabel id="fromPageLabel">Select Language</InputLabel>
                <Select
                  labelId="fromPageLabel"
                  id="fromPage"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  label="Select Language"
                >
                  {languageMenuItems.map((item, index) => (
                    <MenuItem key={index} value={item?.label}>
                      <Tooltip zIndex={9999999} color={'#3d5f8f'} title={item?.label}>
                        {item?.label?.length > 40
                          ? `${item?.label?.substring(0, 40)}...`
                          : item?.label}
                      </Tooltip>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Row>

            <Row gutter={[24, 24]} style={{ marginTop: 20, marginRight: 0, marginLeft: 0 }}>
              <Button
                fullWidth
                color="primary"
                disabled={disable}
                variant="contained"
                onClick={() => (!disable ? handleGenerate() : null)}
              >
                Generate
              </Button>
            </Row>
          </ModalContent>
        </Modal>
      </SentryWrap>
    );
});

export default ResourseAssignmentAiModal;

const ModalContent = styled.div`
  left: 50%;
  max-width: 544px;
  min-width: 450px;
  padding: 0px 24px;
  position: absolute;
  max-height: 70vh;
  top: 50%;
  transform: translate(-50%, -50%);
  background-color: #fff;
  padding: 20px;
  @media only screen and (max-width: 600px) {
    min-width: 90% !important;
  }
`;

const TitleText = styled.span`
  font-size: 18px;
  color: black;
  font-weight: 600;
  color:${p=>p.color?p.color:"black"};
`;